import type { AnimCtx, BodyFn, EaseFn, InstantBodyFn, Segment, Timeline, TimelineAPI } from './types';

export function timeline(build: (api: TimelineAPI<AnimCtx>) => void): Timeline<AnimCtx> {
    const segs: Segment<AnimCtx>[] = [];
    const marks: Record<string, number> = Object.create(null);
    let now = 0;

    const api: TimelineAPI<AnimCtx> = {
        seq(fn: () => void) {
            fn();
        },

        mark(name: string) {
            marks[name] = now;
        },

        tween(dur: number, ease: EaseFn | null | undefined, body: BodyFn<AnimCtx>) {
            const start = now, end = start + dur;
            segs.push({ id: 0, start, end, ease: ease ?? ((u: number) => u), body });
            now = end;
        },

        instant(body: InstantBodyFn<AnimCtx>) {
            const t = now;
            // Wrap the simpler (ctx) => void signature to match BodyFn
            segs.push({ id: 0, start: t, end: t, ease: (u) => u, body: (ctx, _u) => body(ctx) });
        },

        wait(dur: number) {
            // Sugar for tween with no-op body - just advances time
            const start = now, end = start + dur;
            segs.push({ id: 0, start, end, ease: (u) => u, body: () => {} });
            now = end;
        },

        with(name: string, offsetMs: number, fn: () => void) {
            const saved = now;
            now = (marks[name] ?? 0) + offsetMs;
            fn();
            now = saved;
        },

        overlap(offsetMs: number, fn: () => void) {
            const saved = now;
            now = now + offsetMs;
            fn();
            now = saved;
        },

        par(fn: () => void) {
            const parStart = now;
            let maxEnd = parStart;
            let inParallelChild = false;

            // Store original methods
            const originalSeq = api.seq;
            const originalTween = api.tween;
            const originalInstant = api.instant;
            const originalWait = api.wait;
            const originalPar = api.par;
            const originalOverlap = api.overlap;
            const originalWith = api.with;

            // Helper to wrap a parallel branch execution
            const runBranch = <T>(branchFn: () => T): T => {
                if (inParallelChild) {
                    // Already inside a branch (e.g., nested seq inside par), don't reset
                    return branchFn();
                }
                inParallelChild = true;
                now = parStart;  // Reset to parallel start
                const result = branchFn();
                maxEnd = Math.max(maxEnd, now);
                now = parStart;  // Reset for next parallel item
                inParallelChild = false;
                return result;
            };

            // Wrap seq to treat it as a single parallel branch
            api.seq = (seqFn: () => void) => {
                runBranch(() => originalSeq.call(api, seqFn));
            };

            // Override tween to reset now after each tween
            api.tween = (dur: number, ease: EaseFn | null | undefined, body: BodyFn<AnimCtx>) => {
                runBranch(() => originalTween.call(api, dur, ease, body));
            };

            // Override instant
            api.instant = (body: InstantBodyFn<AnimCtx>) => {
                runBranch(() => originalInstant.call(api, body));
            };

            // Override wait
            api.wait = (dur: number) => {
                runBranch(() => originalWait.call(api, dur));
            };

            // Override par (nested parallel blocks)
            api.par = (parFn: () => void) => {
                runBranch(() => originalPar.call(api, parFn));
            };

            // Override overlap
            api.overlap = (offsetMs: number, overlapFn: () => void) => {
                runBranch(() => originalOverlap.call(api, offsetMs, overlapFn));
            };

            // Override with
            api.with = (name: string, offsetMs: number, withFn: () => void) => {
                runBranch(() => originalWith.call(api, name, offsetMs, withFn));
            };

            fn();

            // Restore original methods
            api.seq = originalSeq;
            api.tween = originalTween;
            api.instant = originalInstant;
            api.wait = originalWait;
            api.par = originalPar;
            api.overlap = originalOverlap;
            api.with = originalWith;

            now = maxEnd;
        },
    };

    build(api);

    segs.sort((a, b) => a.start - b.start);
    segs.forEach((s, index) => { s.id = index; });
    const duration = segs.reduce((m, s) => Math.max(m, s.end), 0);

    return {
        duration,
        segs,

        evaluate(tMs: number, ctx: AnimCtx) {
            // Fallback: contexts without multi-pass support get the classic
            // single pass where every started segment is applied once.
            if (!ctx.beginTimelinePass || !ctx.endTimelinePass) {
                for (const s of segs) {
                    if (tMs < s.start) continue;
                    const uRaw = Math.min(1, (tMs - s.start) / (s.end - s.start || 1));
                    const u = s.ease(Math.max(0, uRaw));
                    s.body(ctx, u);
                }
                return;
            }

            const runPass = (passTime: number, freezeSegmentIds: number[]) => {
                ctx.beginTimelinePass!();
                for (const s of segs) {
                    if (passTime < s.start) continue;
                    const uRaw = Math.min(1, (passTime - s.start) / (s.end - s.start || 1));
                    const u = s.ease(Math.max(0, uRaw));
                    ctx.beginSegmentScope?.(s.id);
                    s.body(ctx, u);
                }
                ctx.endTimelinePass!(freezeSegmentIds);
            };

            // Freeze motions at each distinct end time that has elapsed, in
            // ascending order, so an ended motion's constraints hold their
            // end-of-motion state (evaluated with other motions at their true
            // progress) rather than tracking targets that keep moving later.
            const endTimes = [...new Set(
                segs.filter(s => s.start <= tMs && s.end <= tMs).map(s => s.end),
            )].sort((a, b) => a - b);

            for (const endTime of endTimes) {
                const freezeIds = segs.filter(s => s.end === endTime).map(s => s.id);
                runPass(endTime, freezeIds);
            }

            // Final pass at the display time; nothing new to freeze.
            runPass(tMs, []);
        },
    };
}
